import { z } from "zod";

import { isExpired, normalizePermissions, type Session } from "./types";

export const SESSION_STORAGE_KEY = "tcg.admin.session";

const sessionSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.number().int().positive(),
  user: z.object({
    id: z.string().min(1),
    displayName: z.string(),
    email: z.string(),
    roles: z.array(z.string()),
    // unknown strings (e.g. from an older build) are dropped, aliases resolved to canonical names
    permissions: z.array(z.string()).transform(normalizePermissions),
    locale: z.string().optional(),
    totpEnabled: z.boolean().optional(),
    locationCodes: z.array(z.string()).optional(),
  }),
  sessionId: z.string().optional(),
});

/**
 * Session persistence for the SPA **dev bypass / tests only** (`AuthApi.persistSession`):
 * `sessionStorage` = per tab, cleared when the tab closes. With identity-service the access token
 * stays in memory and a reload restores it through the HttpOnly refresh cookie (design/11 §2,
 * T11-02) — nothing is written here.
 */
export function loadSession(
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): Session | null {
  const raw = storage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed: Session = sessionSchema.parse(JSON.parse(raw));
    if (isExpired(parsed, now)) {
      storage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

export function saveSession(session: Session, storage: Storage = window.sessionStorage): void {
  storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(storage: Storage = window.sessionStorage): void {
  storage.removeItem(SESSION_STORAGE_KEY);
}
